import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, Mic, Users, BookOpen, GraduationCap, Mail, MessageSquare, Bug, Handshake, Shield, Lock, HelpCircle, Building2, Briefcase, ShieldCheck, BarChart3, PlayCircle, UserPlus, Eye, LifeBuoy } from "lucide-react";
import { PRICING_LINK_VISIBLE } from "@/lib/featureFlags";
import { IOS_CLIENT, linkForClient } from "@/lib/guideClient";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

// Exported for the drift guard in OrganizationPages.test.jsx: every tile here has
// to resolve to a real /features/:slug entry, or the dropdown quietly bounces
// visitors to the home page.
export const productItems = {
  instructor: [
    { icon: Mic, title: "Lesson summaries", desc: "Record lessons, then review and share clear summaries", to: "/features/lesson-summaries" },
    { icon: Users, title: "Student Management", desc: "Organized profiles with lesson history and progress", to: "/features/student-management" },
    { icon: BarChart3, title: "Lesson History & Reporting", desc: "Every lesson you've taught, with totals and a spreadsheet export", to: "/features/lesson-history" },
    { icon: BookOpen, title: "Repertoire Assistant", desc: "Teaching guides tailored to your students", to: "/features/repertoire-assistant" },
    { icon: UserPlus, title: "Joining a Program", desc: "Accept an invite or find work, in one short video", to: "/features/joining-a-program" },
    { icon: Mail, title: "Email Summaries", desc: "Send polished lesson summaries with one click", to: "/features/email-summaries" },
  ],
  student: [
    { icon: GraduationCap, title: "Student Dashboard", desc: "Your personal lesson hub and progress tracker", to: "/features/student-portal" },
    { icon: BookOpen, title: "Rehearsals", desc: "Record your practice and get instructor feedback", to: "/features/rehearsals" },
    { icon: Shield, title: "Portfolio", desc: "Showcase your musical journey and achievements", to: "/features/portfolio" },
  ],
  organization: [
    // The overview leads: a director arriving cold wants the whole picture
    // before a single capability. The three below are its deep-dives.
    { icon: Building2, title: "Hans for Organizations", desc: "The whole platform, from publishing a program to the export", to: "/for-organizations" },
    { icon: BarChart3, title: "Organization Portal", desc: "Oversight of every instructor from one login", to: "/features/organization-portal" },
    { icon: Briefcase, title: "Programs & Classes", desc: "Publish a program, staff it, and schedule the term", to: "/features/organization-programs" },
    { icon: ShieldCheck, title: "Consent & Compliance", desc: "COPPA-safe recording with an exportable ledger", to: "/features/organization-compliance" },
  ],
};

/* Marketing chrome, not the Studio Quiet app surface: this label has to match
   the legacy columns it sits beside, so the raw palette class is deliberate. */
const NavSectionLabel = ({ children }) => (
  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2 px-3">{children}</p> /* brand-lint-ignore */
);

// `ios: true` marks a page that has an iOS mode and sells nothing; a page
// opened from the iOS app links only to those (see resourceItemsFor).
const resourceItems = {
  getInvolved: [
    { icon: MessageSquare, title: "Submit Feedback", desc: "Help us improve Hans with your ideas", to: "/feedback", ios: true },
    { icon: Bug, title: "Report a Bug", desc: "Let us know if something isn't working", to: "/feedback?type=bug", ios: true },
    { icon: Handshake, title: "Partnership Inquiries", desc: "Explore collaboration opportunities", to: "/contact", ios: true },
    { icon: LifeBuoy, title: "Support", desc: "Account, billing and help by email", to: "/support", ios: true },
  ],
  // One Academy: its video walkthroughs first, then a written guide per
  // audience. Each guide documents only the portal its reader can sign in to,
  // so a student sent to the instructor guide doesn't read about rosters and
  // consent requests they will never see.
  academy: [
    { icon: PlayCircle, title: "Onboarding", desc: "Short video walkthroughs of the real app", to: "/academy/onboarding" },
    { icon: HelpCircle, title: "For instructors", desc: "Learn how to get the most out of Hans", to: "/guide", ios: true },
    { icon: GraduationCap, title: "For students & parents", desc: "Using the student portal", to: "/student-guide", ios: true },
    { icon: Building2, title: "For organizations", desc: "Running programs and classes", to: "/org-guide" },
    { icon: Eye, title: "For program viewers", desc: "Following a program you were given", to: "/viewer-guide" },
  ],
  legal: [
    { icon: Lock, title: "Privacy Policy", desc: "How we handle and protect your data", to: "/privacy", ios: true },
    { icon: Shield, title: "Security", desc: "Our security practices and infrastructure", to: "/security", ios: true },
  ],
};

/**
 * The Resources links for a client. The iOS app gets only the ones marked
 * `ios`, each carrying `client=ios` so the next page keeps the app's mode.
 */
const resourceItemsFor = (client) => {
  if (client !== IOS_CLIENT) return resourceItems;
  const keep = (items) => items.filter((item) => item.ios).map((item) => ({ ...item, to: linkForClient(item.to, client) }));
  return { getInvolved: keep(resourceItems.getInvolved), academy: keep(resourceItems.academy), legal: keep(resourceItems.legal) };
};

/**
 * The logo, linking home. The iOS app gets it without the link: the home page
 * leads to sign-up, and from there to the web plans.
 */
const HomeLink = ({ ios, children }) =>
  ios ? (
    <div className="flex items-center gap-3">{children}</div>
  ) : (
    <Link to="/" className="flex items-center gap-3">
      {children}
    </Link>
  );

const NavTile = ({ icon: Icon, title, desc, to }) => (
  <NavigationMenuPrimitive.Link asChild>
    <Link
      to={to}
      className="block select-none rounded-xl p-3 leading-none no-underline outline-hidden transition-colors hover:bg-white/5 focus:bg-white/5 group"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-[#f4cf08]/10 transition-colors">
          <Icon className="w-4 h-4 text-zinc-400 group-hover:text-[#f4cf08] transition-colors" strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-sm font-medium text-white mb-0.5">{title}</div>
          <p className="text-xs text-zinc-500 leading-snug">{desc}</p>
        </div>
      </div>
    </Link>
  </NavigationMenuPrimitive.Link>
);

const MobileSection = ({ title, items, onClose }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full py-3 px-1 text-sm font-medium text-white"
      >
        {title}
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-3 space-y-1">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onClose}
              className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-white/5 transition-colors"
            >
              <item.icon className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
              <span className="text-sm text-zinc-300">{item.title}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * `client` is the app the page was opened from (lib/guideClient). For the iOS
 * app the navbar drops every link that sells a plan or leads to a page that
 * does: Product, Pricing, Log in and the logo's link home.
 */
const PublicNavbar = ({ client = null }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const ios = client === IOS_CLIENT;
  const resources = resourceItemsFor(client);

  return (
    <nav className="relative z-50 flex items-center justify-between px-4 md:px-8 py-4 md:py-6 max-w-[90rem] mx-auto safe-area-padding">
      {/* Logo */}
      <HomeLink ios={ios}>
        <img src="/logo192.png" alt="Hans" className="h-9 md:h-10 rounded-lg" />
        <span className="text-lg font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Hans</span>
      </HomeLink>

      {/* Desktop nav — centered */}
      <div className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
        <NavigationMenuPrimitive.Root className="relative z-10 flex items-center justify-center">
          <NavigationMenuPrimitive.List className="flex list-none items-center gap-1">
            {/* Product dropdown — not in the iOS app. Left at its original
                indentation for the reason given on the Pricing item below. */}
            {!ios && (
            <NavigationMenuPrimitive.Item>
              <NavigationMenuPrimitive.Trigger className="group inline-flex items-center gap-1 bg-transparent text-zinc-300 hover:text-white hover:bg-white/5 data-[state=open]:bg-white/5 data-[state=open]:text-white rounded-full px-4 py-2 text-sm font-medium transition-colors outline-hidden">
                Product
                <ChevronDown className="w-3 h-3 transition-transform duration-200 group-data-[state=open]:rotate-180" aria-hidden />
              </NavigationMenuPrimitive.Trigger>
              {/* Three columns no longer fit anchored to the trigger, so the panel is
                  centered on the viewport. The translate lives on an inner wrapper, NOT
                  on Content: the enter/exit animations own Content's transform and would
                  drop the centering for the length of the animation. */}
              <NavigationMenuPrimitive.Content className="absolute left-1/2 top-full mt-2 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
                <div className="-translate-x-1/2 w-[min(840px,calc(100vw-2rem))]">
                  <div className="rounded-2xl border border-[var(--studio-divider-strong)] bg-[var(--studio-base)]/95 p-4 shadow-2xl backdrop-blur-xl">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <NavSectionLabel>For Instructors</NavSectionLabel>
                        {productItems.instructor.map((item) => (
                          <NavTile key={item.to} {...item} />
                        ))}
                      </div>
                      <div>
                        <NavSectionLabel>For Students</NavSectionLabel>
                        {productItems.student.map((item) => (
                          <NavTile key={item.to} {...item} />
                        ))}
                      </div>
                      <div>
                        <NavSectionLabel>For Organizations</NavSectionLabel>
                        {productItems.organization.map((item) => (
                          <NavTile key={item.to} {...item} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </NavigationMenuPrimitive.Content>
            </NavigationMenuPrimitive.Item>
            )}

            {/* Resources dropdown: centered under its own trigger, so the Item is the
                positioning box. The width leaves room for the trigger sitting right of
                the viewport's center, so the panel never runs off a tablet screen. */}
            <NavigationMenuPrimitive.Item className="relative">
              <NavigationMenuPrimitive.Trigger className="group inline-flex items-center gap-1 bg-transparent text-zinc-300 hover:text-white hover:bg-white/5 data-[state=open]:bg-white/5 data-[state=open]:text-white rounded-full px-4 py-2 text-sm font-medium transition-colors outline-hidden">
                Resources
                <ChevronDown className="w-3 h-3 transition-transform duration-200 group-data-[state=open]:rotate-180" aria-hidden />
              </NavigationMenuPrimitive.Trigger>
              <NavigationMenuPrimitive.Content className="absolute left-1/2 top-full mt-2 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
                <div className="-translate-x-1/2 w-[min(780px,calc(100vw-10rem))] rounded-2xl border border-[var(--studio-divider-strong)] bg-[var(--studio-base)]/95 p-4 shadow-2xl backdrop-blur-xl" data-testid="resources-panel">
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <NavSectionLabel>Get Involved</NavSectionLabel>
                      {resources.getInvolved.map((item) => (
                        <NavTile key={item.to} {...item} />
                      ))}
                    </div>
                    <div>
                      <NavSectionLabel>Hans Academy</NavSectionLabel>
                      {resources.academy.map((item) => (
                        <NavTile key={item.to} {...item} />
                      ))}
                    </div>
                    <div>
                      <NavSectionLabel>Legal</NavSectionLabel>
                      {resources.legal.map((item) => (
                        <NavTile key={item.to} {...item} />
                      ))}
                    </div>
                  </div>
                </div>
              </NavigationMenuPrimitive.Content>
            </NavigationMenuPrimitive.Item>

            {/* Pricing — hidden while PRICING_LINK_VISIBLE is off; the route stays live.
                The block below is left at its original indentation on purpose: it is
                untouched legacy markup, and re-indenting it would re-add its raw
                zinc/white classes as "new" lines to the brand lint. */}
            {PRICING_LINK_VISIBLE && !ios && (
            <NavigationMenuPrimitive.Item>
              <NavigationMenuPrimitive.Link asChild>
                <Link
                  to="/pricing"
                  className={`inline-flex h-9 items-center justify-center rounded-full px-4 py-2 text-sm font-medium transition-colors hover:text-white hover:bg-white/5 ${
                    location.pathname === "/pricing" ? "text-white" : "text-zinc-300"
                  }`}
                >
                  Pricing
                </Link>
              </NavigationMenuPrimitive.Link>
            </NavigationMenuPrimitive.Item>
            )}
          </NavigationMenuPrimitive.List>
        </NavigationMenuPrimitive.Root>
      </div>

      {/* Desktop login — ONE button for all three audiences. Which portal you
          want is asked once, on the sign-in page's tab strip, rather than by
          stacking a button per audience in a navbar that has to fit them all.
          Not in the iOS app: the signed-in web pages sell plans. */}
      {!ios && (
        <div className="hidden md:flex items-center gap-3">
          <Link
            data-testid="login-button"
            to="/login"
            className="rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)]
                       px-5 py-2 text-sm font-medium text-[var(--text-primary)] transition-all
                       hover:bg-[var(--studio-overlay)]"
          >
            Log in
          </Link>
        </div>
      )}

      {/* Mobile hamburger */}
      <div className="md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button className="p-2 text-zinc-400 hover:text-white transition-colors" aria-label="Open menu">
              <Menu className="w-6 h-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-zinc-950 border-white/10 w-[300px] p-6 overflow-y-auto">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <div className="flex items-center gap-3 mb-8">
              <img src="/logo192.png" alt="Hans" className="h-8 rounded-lg" />
              <span className="text-lg font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Hans</span>
            </div>

            {!ios && (
              <MobileSection
                title="Product"
                items={[...productItems.instructor, ...productItems.student, ...productItems.organization]}
                onClose={() => setMobileOpen(false)}
              />
            )}

            <MobileSection title="Resources" items={[...resources.getInvolved, ...resources.academy, ...resources.legal]} onClose={() => setMobileOpen(false)} />

            {/* Original indentation kept — see the note on the desktop Pricing item above. */}
            {PRICING_LINK_VISIBLE && !ios && (
            <Link to="/pricing" onClick={() => setMobileOpen(false)} className="block py-3 px-1 text-sm font-medium text-white border-b border-white/5">
              Pricing
            </Link>
            )}

            {!ios && (
              <div className="mt-6">
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="block w-full rounded-full border border-[var(--studio-divider-strong)]
                             bg-[var(--studio-raised)] px-5 py-2.5 text-center text-sm font-medium
                             text-[var(--text-primary)] transition-all hover:bg-[var(--studio-overlay)]"
                >
                  Log in
                </Link>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};

export default PublicNavbar;
